import { useTranslations } from '@aikoa/i18n/react'
import { useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import { Dimensions, View } from 'react-native'
import { type Action } from '~/components/Actions'
import AppActions from '~/components/AppActions'
import { FeedSection } from '~/components/feed/FeedSection'
import { InviteTeamMemberBox } from '~/components/home/InviteTeamMemberBox'
import Icon from '~/components/Icon'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox'
import { LoadingView } from '~/components/LoadingView'
import { ScrollView } from '~/components/ScrollView'
import { Body } from '~/components/typography/Content'
import { Heading1 } from '~/components/typography/Titles'
import { InboundEmailView } from '~/components/user/realtors/InboundEmailView'
import { OrganisationBox } from '~/components/user/realtors/OrganisationBox'
import { useHasFeedContent } from '~/data/feed/useFeed'
import { cn } from '~/lib/utils'

const windowWidth = Dimensions.get('window').width

export function RealtorHomeView() {
  const { t } = useTranslations('home')
  const { hasFeedContent, isLoadingFeedContent } = useHasFeedContent()

  const { view } = useLocalSearchParams<{
    view?: 'leads' | 'assets'
  }>()

  const isActionsFloating = hasFeedContent && windowWidth < 640
  const toolbarActionClassName = cn(
    'flex-1 items-center justify-center py-7',
    isActionsFloating && 'py-5 text-background hover:bg-secondary'
  )

  const topActions = useMemo(
    () =>
      [
        {
          key: 'create-feedback-request',
          label: t(($) => $.realtor.createFeedbackLabel),
          actionType: 'add',
          description: !hasFeedContent ? t(($) => $.realtor.createFeedbackDescription) : undefined,
          icon: 'comment-plus-outline',
          iconSize: isActionsFloating ? 18 : 24,
          iconLibrary: 'materialcommunityicons',
          href: '/feedback-request/add',
          theme: '#D5FBCF',
          className: hasFeedContent ? cn(toolbarActionClassName, 'rounded-r-none') : undefined,
          buttonVariant: isActionsFloating ? 'default' : undefined,
        },
        {
          key: 'add-property',
          label: t(($) => $.realtor.addPropertyLabel),
          description: !hasFeedContent ? t(($) => $.realtor.addPropertyDescription) : undefined,
          icon: 'home-plus-outline',
          iconSize: isActionsFloating ? 18 : 24,
          iconLibrary: 'materialcommunityicons',
          actionType: 'add',
          href: {
            pathname: `/property/add`,
          },
          theme: '#D5FBCF',
          className: hasFeedContent ? cn(toolbarActionClassName, 'rounded-l-none') : undefined,
          buttonVariant: isActionsFloating ? 'default' : undefined,
        },
      ] satisfies Action[],
    [hasFeedContent, toolbarActionClassName, t, isActionsFloating]
  )

  const actions = (
    <AppActions
      variant={isActionsFloating ? 'default' : 'transparent'}
      toolbarClassName={cn(
        'p-0! gap-0 rounded-none border-0 bg-transparent',
        isActionsFloating && 'mt-0 bg-primary'
      )}
      className={cn(
        'rounded-content bg-neutral-200/60 backdrop-blur-sm',
        hasFeedContent && 'overflow-hidden rounded-full py-0'
      )}
      actions={topActions}
    />
  )

  return (
    <ScrollView
      className={cn('flex-1 rounded-content transition-opacity duration-150')}
      offsetTrackHeight
      contentContainerClassName="relative gap-3 pt-3"
      footer={isActionsFloating && actions}
      footerClassName="mt-0">
      <OrganisationBox showOnlyForUnverifiedEmail />
      {isLoadingFeedContent ? <LoadingView /> : !isActionsFloating && actions}
      <FeedSection initialView={view} />
      <ContentBox
        scrollable={false}
        variant="default"
        contentContainerClassName="gap-4 px-0 pt-0"
        header={
          <BoxActionsHeader variant="default">
            <View className="flex-row items-baseline gap-2 px-4">
              <Icon name="settings-outline" size={20} className="flex-none shrink-0 text-accent" />
              <Heading1 className="flex-auto text-center">
                {t(($) => $.realtor.automaticImportTitle)}
              </Heading1>
            </View>
          </BoxActionsHeader>
        }>
        <Body className="px-6 text-center text-base/normal text-primary">
          {t(($) => $.realtor.automaticImportDescription)}
        </Body>
        <InboundEmailView className="px-6" />
      </ContentBox>
      <InviteTeamMemberBox />
    </ScrollView>
  )
}
